/**
 * BASELINE — Semana 0.
 *
 * Va antes del Día 1. Mide para no entrenar problemas imaginarios.
 *
 * Terminología obligatoria: «dificultad principal», nunca «cuello de botella».
 *
 * CAMBIO PEDAGÓGICO 2 (aprobado): el paso final siembra el Learning Bank con
 * elementos estructurados, no con texto libre. Solo dificultades realmente
 * observadas y confirmadas. No se inventan problemas para llenar el banco.
 *
 * DECISIÓN A (aprobada): el paso de listening usa una MUESTRA AUTÉNTICA —un
 * clip nuevo y desconocido— y no una búsqueda de frase en YouGlish. Saber de
 * antemano qué frase buscaste contamina la medición.
 */

import type { Sesion } from '../../tipos.js';
import { FALLBACK_SIN_FUENTE } from '../fuentesListening.js';

/** Definición que la aplicación muestra al pedir las dificultades principales. */
export const QUE_ES_UNA_DIFICULTAD_PRINCIPAL =
  'Una dificultad principal es algo que realmente te frenó: por ejemplo, no entender habla rápida, tardar buscando una palabra, quedarte en blanco, no poder reformular, repetir demasiado «uh/um», usar siempre la misma frase o tener una pronunciación problemática.';

export const BASELINE: Sesion = {
  clave: 'baseline',
  tipo: 'baseline',
  etiqueta: 'Baseline',
  titulo: 'Baseline · medir antes de entrenar',
  corto: 'Baseline',
  objetivo:
    'Descubrir tus dificultades principales reales, para no entrenar problemas imaginarios.',
  porQue:
    'El peso que tendrán listening, pronunciación, recuperación léxica y conversación se ajusta a lo que salga hoy. Por eso medimos antes de entrenar.',
  areasAEvaluar: [
    'habla_espontanea',
    'listening',
    'recuperacion_lexica',
    'naturalidad',
    'interaccion_turnos',
  ],
  capturaCorrecciones: true,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'baseline-muestra',
          titulo: 'Muestra espontánea',
          minutosSugeridos: 2,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Guardar una muestra limpia de tu inglés de hoy.',
          porQue:
            'Es tu Semana 0. Dentro de cuatro, ocho y doce semanas vamos a comparar contra esta grabación, no contra tu recuerdo de cómo hablabas.',
          instrucciones: [
            'Hablá 2 minutos sobre lo que quieras, sin preparar el tema.',
            'No repitas si te equivocás: los errores también son parte de la medición.',
            'Guardala con la etiqueta «Baseline W0».',
          ],
          uso: {
            queHacer:
              'Grabá 2 minutos de habla espontánea y guardala como «Baseline W0». Si no diste permiso al micrófono, grabá con la app de tu teléfono y cargá el archivo.',
          },
          areas: ['habla_espontanea', 'pronunciacion_prosodia'],
        },
        {
          clave: 'baseline-voice',
          titulo: 'Baseline por Voice',
          minutosSugeridos: 12,
          herramienta: 'chatgpt',
          prompt: 'baseline',
          objetivo:
            'Observar fluidez, recuperación léxica, interacción y naturalidad bajo tareas que no preparaste.',
          porQue:
            'Las tareas impredecibles muestran mejor tu capacidad real que un texto ensayado. Lo que sale sin preparar es lo que tenés disponible de verdad.',
          instrucciones: [
            'Copiá el prompt y abrí ChatGPT.',
            'Entrá en modo Voice.',
            'No te prepares: respondé como te salga.',
            'No pidas correcciones durante la conversación.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y hacé toda la conversación sin pedir correcciones. El resumen viene al final.',
          },
          areas: [
            'habla_espontanea',
            'recuperacion_lexica',
            'naturalidad',
            'interaccion_turnos',
            'gramatica_fluidez',
          ],
        },
        {
          clave: 'baseline-dificultades',
          titulo: 'Tus dificultades principales',
          minutosSugeridos: 6,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo:
            'Si terminás antes de los 6 minutos, avanzá. No hace falta agotar el tiempo.',
          objetivo:
            'Identificar qué fue lo que más te costó, para que el programa entrene eso y no una lista genérica.',
          porQue:
            'Acá no hacés otro ejercicio: revisás lo que acaba de pasar. Estas dificultades son la semilla de tu Learning Bank y de lo que el Día 5 va a priorizar.',
          instrucciones: [
            'Pensá en la sesión que acabás de hacer.',
            'Elegí hasta 3 cosas que más te costaron. ' + QUE_ES_UNA_DIFICULTAD_PRINCIPAL,
            'Guardá cada una en el Learning Bank y, si la reconocés, elegí a qué área pertenece.',
            'Si no estás seguro del área de alguna, dejala sin clasificar. Es una opción válida.',
            'Si solo notaste una dificultad real, guardá una. No inventes para llegar a tres.',
          ],
          advertencia:
            'Guardá únicamente dificultades que realmente notaste. Un banco con problemas inventados hace que el Día 5 te recomiende trabajar cosas que no te pasan.',
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'baseline-muestra',
          titulo: 'Muestra espontánea',
          minutosSugeridos: 2,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Guardar una muestra limpia de tu inglés de hoy.',
          porQue: 'Es la referencia de Semana 0 contra la que compararemos en W4, W8 y W12.',
          instrucciones: [
            'Hablá 2 minutos sin preparar el tema.',
            'No repitas si te equivocás.',
            'Guardala con la etiqueta «Baseline W0».',
          ],
          uso: {
            queHacer:
              'Grabá 2 minutos de habla espontánea y guardala como «Baseline W0». Si no diste permiso al micrófono, grabá con la app de tu teléfono y cargá el archivo.',
          },
          areas: ['habla_espontanea', 'pronunciacion_prosodia'],
        },
        {
          clave: 'baseline-voice',
          titulo: 'Baseline por Voice',
          minutosSugeridos: 20,
          herramienta: 'chatgpt',
          prompt: 'baseline',
          objetivo: 'Evaluar tu producción espontánea en varias funciones distintas.',
          porQue:
            'Conversación, historia, opinión y role-play revelan fortalezas diferentes. Medir solo una función daría una foto incompleta.',
          instrucciones: [
            'Copiá el prompt y abrí ChatGPT en modo Voice.',
            'Hacé toda la batería sin preparación.',
            'No pidas correcciones hasta el final.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y completá conversación, historia, explicación, opinión y role-play sin coaching.',
          },
          areas: [
            'habla_espontanea',
            'recuperacion_lexica',
            'naturalidad',
            'interaccion_turnos',
            'gramatica_fluidez',
          ],
        },
        {
          clave: 'baseline-listening',
          titulo: 'Muestra auténtica de listening',
          minutosSugeridos: 8,
          herramienta: 'muestraListening',
          objetivo: 'Medir cuánto entendés de habla real que no conocés de antemano.',
          porQue:
            'El listening se mide aparte: hablar bien no garantiza entender habla rápida. Y el fragmento tiene que ser desconocido — si ya sabés de qué va, medís memoria y no comprensión.',
          instrucciones: [
            'Abrí un clip NUEVO de entre 30 y 90 segundos de inglés estadounidense natural. Que no lo hayas escuchado antes.',
            'Primera escucha SIN subtítulos ni transcripción. Ni una mirada al texto.',
            'Resumí en inglés lo que entendiste. En voz alta o escrito, como prefieras.',
            'Recién ahora comprobá con los subtítulos o la transcripción, si los hay.',
            'Anotá qué tan difícil te resultó, y si lo que fallaste fue vocabulario que no conocías o sonidos que no distinguiste. No es lo mismo y se entrenan distinto.',
          ],
          uso: {
            queHacer:
              'Elegí una de las fuentes sugeridas, tomá un fragmento nuevo de 30–90 segundos y escuchalo una vez SIN texto. Resumí en inglés y solo después comprobá con la transcripción.',
            fuentesSugeridas: ['npr', 'thisAmericanLife', 'pbsNewsHour', 'youtubeCanalPropio'],
            fallbackSinFuente: FALLBACK_SIN_FUENTE,
          },
          areas: ['listening'],
        },
        {
          clave: 'baseline-dificultades',
          titulo: 'Tus dificultades principales',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo:
            'Si terminás antes de los 5 minutos, avanzá. No hace falta agotar el tiempo.',
          objetivo: 'Elegir entre 3 y 5 prioridades reales que guiarán tu programa.',
          porQue:
            'El plan se personaliza con estos datos. Son la semilla del Learning Bank y de lo que el Día 5 priorizará.',
          instrucciones: [
            'Anotá solo problemas repetidos o de alto impacto. ' +
              QUE_ES_UNA_DIFICULTAD_PRINCIPAL,
            'Guardá cada uno en el Learning Bank y, si la reconocés, elegí su área.',
            'Si no estás seguro del área de alguno, dejalo sin clasificar.',
            'No intentes corregir veinte cosas a la vez: entre 3 y 5 es suficiente.',
          ],
          advertencia:
            'Guardá únicamente dificultades que realmente notaste. No inventes para llegar a un número.',
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'baseline-muestra',
          titulo: 'Muestra espontánea',
          minutosSugeridos: 2,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo: 'Guardar la referencia de Semana 0.',
          porQue: 'Permite comparación longitudinal real en W4, W8 y W12.',
          instrucciones: [
            'Hablá 2 minutos sin preparar el tema.',
            'Guardala con la etiqueta «Baseline W0».',
          ],
          uso: {
            queHacer:
              'Grabá 2 minutos de habla espontánea y guardala como «Baseline W0». Si no diste permiso al micrófono, grabá con la app de tu teléfono y cargá el archivo.',
          },
          areas: ['habla_espontanea', 'pronunciacion_prosodia'],
        },
        {
          clave: 'baseline-voice',
          titulo: 'Baseline por Voice',
          minutosSugeridos: 25,
          herramienta: 'chatgpt',
          prompt: 'baseline',
          objetivo: 'Evaluar tu habla espontánea de forma amplia.',
          porQue:
            'La variedad de funciones reduce el riesgo de medir solo tu inglés de trabajo, que es justamente el que ya tenés automatizado.',
          instrucciones: [
            'Copiá el prompt y abrí ChatGPT en modo Voice.',
            'Completá conversación, historia, explicación, opinión y role-play.',
            'No te prepares y no pidas correcciones durante la performance.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y completá las cinco tareas sin coaching. El resumen viene al final.',
          },
          areas: [
            'habla_espontanea',
            'recuperacion_lexica',
            'naturalidad',
            'interaccion_turnos',
            'gramatica_fluidez',
          ],
        },
        {
          clave: 'baseline-listening',
          titulo: 'Muestra auténtica de listening',
          minutosSugeridos: 10,
          herramienta: 'muestraListening',
          objetivo: 'Medir tu comprensión de habla auténtica desconocida, a velocidad normal.',
          porQue:
            'Queremos saber si tu límite está en la percepción, en el vocabulario auditivo o en la velocidad. Son tres problemas distintos con tres soluciones distintas, y solo se distinguen con material que no conocés.',
          instrucciones: [
            'Abrí un clip NUEVO de entre 30 y 90 segundos de inglés estadounidense natural.',
            'Primera escucha SIN subtítulos ni transcripción.',
            'Resumí en inglés lo que entendiste.',
            'Segunda escucha, todavía sin texto. Fijate qué recuperás en la segunda pasada.',
            'Recién ahora comprobá con la transcripción o los subtítulos, si están disponibles.',
            'Anotá la dificultad percibida y si el fallo fue de vocabulario o de percepción de sonidos.',
          ],
          uso: {
            queHacer:
              'Elegí una de las fuentes sugeridas, tomá un fragmento nuevo de 30–90 segundos y escuchalo DOS veces sin texto, resumiendo entre medio. Comprobá con la transcripción solo al final.',
            fuentesSugeridas: [
              'npr',
              'thisAmericanLife',
              'pbsNewsHour',
              'youtubeCanalPropio',
              'podcastPropio',
            ],
            fallbackSinFuente: FALLBACK_SIN_FUENTE,
          },
          areas: ['listening'],
        },
        {
          clave: 'baseline-lectura-vs-habla',
          titulo: 'Lectura vs improvisación',
          minutosSugeridos: 8,
          herramienta: 'grabadora',
          grabadora: true,
          objetivo:
            'Separar tu pronunciación al leer de tu pronunciación al improvisar.',
          porQue:
            'Si leés bien pero al improvisar empeora, el problema es de automaticidad y no de articulación. Son dos cosas distintas y se entrenan distinto.',
          instrucciones: [
            'Leé en voz alta un párrafo corto en inglés y grabate.',
            'Después explicá lo mismo con tus palabras, sin mirar, y grabate otra vez.',
            'Escuchá las dos muestras seguidas.',
            'Anotá si notás diferencia entre las dos.',
          ],
          uso: {
            queHacer:
              'Grabá dos muestras: una leyendo un párrafo y otra explicándolo con tus palabras sin mirar. Escuchá las dos y compará.',
          },
          areas: ['pronunciacion_prosodia', 'habla_espontanea'],
        },
        {
          clave: 'baseline-dificultades',
          titulo: 'Tus dificultades principales',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo:
            'Si terminás antes de los 5 minutos, avanzá. No hace falta agotar el tiempo.',
          objetivo: 'Definir las prioridades que guiarán las próximas doce semanas.',
          porQue:
            'Entrenaremos lo que aparezca hoy, no una lista genérica. Estos elementos alimentan el Día 4 y la recomendación del Día 5.',
          instrucciones: [
            'Guardá entre 3 y 5 dificultades de alto impacto. ' +
              QUE_ES_UNA_DIFICULTAD_PRINCIPAL,
            'Elegí el área de cada una si la reconocés; si no, dejala sin clasificar.',
            'Priorizá lo que se repitió durante la sesión sobre lo que pasó una sola vez.',
          ],
          advertencia:
            'Guardá únicamente dificultades que realmente notaste. No inventes para llegar a un número.',
        },
      ],
    },
  },
};
